import { atom } from 'jotai'
import type { WritableAtom } from 'jotai'
import AsyncStorage from '@react-native-async-storage/async-storage'
import type { z } from 'zod'

interface AtomWithAsyncStorageOptions<T> {
  /** Zod schema for validation on hydration */
  schema?: z.ZodType<T>
  /** Called when validation fails */
  onValidationError?: (error: z.ZodError, rawValue: unknown) => void
  /** Called when JSON parsing fails */
  onParseError?: (error: Error, rawString: string) => void
}

/**
 * Creates a Jotai atom that persists to AsyncStorage with optional Zod validation.
 *
 * Features:
 * - Type-safe with generic parameter
 * - Optional Zod schema validation on hydration
 * - Graceful fallback to initial value on corrupted data
 * - Console warnings for debugging
 *
 * @example
 * ```typescript
 * const userLocaleAtom = atomWithAsyncStorage<string>(
 *   'userLocale',
 *   'en',
 *   { schema: z.string() }
 * )
 * ```
 */
export function atomWithAsyncStorage<T>(
  key: string,
  initialValue: T,
  options: AtomWithAsyncStorageOptions<T> = {}
): WritableAtom<T, [T | ((prev: T) => T)], void> {
  const { schema, onValidationError, onParseError } = options

  const baseAtom = atom<T>(initialValue)

  baseAtom.onMount = (setValue) => {
    void (async () => {
      try {
        const item = await AsyncStorage.getItem(key)

        if (item === null) {
          // No stored value, use initial
          return
        }

        let parsed: unknown
        try {
          parsed = JSON.parse(item)
        } catch (parseError) {
          console.warn(`[atomWithAsyncStorage] Failed to parse ${key}, clearing:`, parseError)
          onParseError?.(parseError as Error, item)
          // Anti-brick (ATL-569): drop the un-parseable blob so it can never re-poison launch.
          await AsyncStorage.removeItem(key)
          return
        }

        // Validate with Zod if schema provided
        if (schema) {
          const result = schema.safeParse(parsed)
          if (result.success) {
            setValue(result.data)
          } else {
            console.warn(
              `[atomWithAsyncStorage] Validation failed for ${key}:`,
              result.error.format()
            )
            onValidationError?.(result.error, parsed)
            // Fallback to initial value, clear corrupted data
            await AsyncStorage.removeItem(key)
          }
        } else {
          // No schema, trust the parsed value
          setValue(parsed as T)
        }
      } catch (error) {
        // Anti-brick (ATL-569): ANY failure hydrating a launch-path atom clears the
        // persisted value instead of risking a re-crash on the next launch. The 2026-06-04
        // brick came from bad persisted data crashing before the updater could self-heal
        // (fallbackToCacheTimeout). Clear-on-failure guarantees a poisoned blob can't
        // survive across launches.
        console.warn(`[atomWithAsyncStorage] Error hydrating ${key}, clearing:`, error)
        try {
          await AsyncStorage.removeItem(key)
        } catch {
          // Storage itself is unavailable; the in-memory initial value still applies.
        }
      }
    })()
  }

  const derivedAtom = atom(
    (get) => get(baseAtom),
    (get, set, update: T | ((prev: T) => T)) => {
      const nextValue =
        typeof update === 'function'
          ? (update as (prev: T) => T)(get(baseAtom))
          : update

      // Validate before saving if schema provided
      if (schema) {
        const result = schema.safeParse(nextValue)
        if (!result.success) {
          console.error(
            `[atomWithAsyncStorage] Refusing to save invalid data for ${key}:`,
            result.error.format()
          )
          return
        }
      }

      set(baseAtom, nextValue)
      // Persist defensively: a non-serializable value (e.g. a cyclical native Firestore
      // DocumentReference) must NEVER fatally crash the app. The in-memory value is already set
      // above; if stringify/persist fails we keep it in memory and skip writing to storage.
      try {
        void AsyncStorage.setItem(key, JSON.stringify(nextValue))
      } catch (error) {
        console.error(`[atomWithAsyncStorage] Failed to persist "${key}" (kept in memory):`, error)
      }
    }
  )

  return derivedAtom
}
